import { HttpMethod } from '@previo/core'
import { APIPaths } from '~/http/APIPaths'
import Http from '~/http/Http'
import { usePropertyStore } from '~/store/property/property'
import type {
  BookOnlineSettingStatusResponse,
  OnlineBookingLatestByResponse,
  OnlineReservationFormConfiguration,
  OnlineReservationFormConfigurationCreate,
  OnlineReservationFormConfigurationCreateResponse,
  OnlineReservationFormConfigurationList,
  OnlineReservationFormConfigurationResponse,
  OnlineReservationFormConfigurationUpdate,
  OnlineReservationFormConfigurationsResponse,
} from '~/http/setting/onlineReservation/booking/types'
import type { SettingDTO } from '~/pages/setting/applications/online-reservation/booking/store/API/DTO/SettingDTO'

export class OnlineReservationApi extends Http {
  getOnlineBookingLatestBy(): Promise<OnlineBookingLatestByResponse> {
    return this.callAuthWithPropertyAPI<null, OnlineBookingLatestByResponse>(HttpMethod.GET, {
      path: APIPaths.ONLINE_RESERVATION_BOOKING_LATEST_BY,
    })
  }

  getBookOnlineSetting(): Promise<BookOnlineSettingStatusResponse> {
    return this.callAuthWithPropertyAPI<null, BookOnlineSettingStatusResponse>(HttpMethod.GET, {
      path: APIPaths.PROPERTY_SETTINGS_BOOK_ONLINE,
    })
  }

  update = (settingsApi: SettingDTO): Promise<null> => {
    const propertyStore = usePropertyStore()

    return this.callAuthWithPropertyAPI<SettingDTO, null>(
      HttpMethod.PUT,
      {
        path: APIPaths.ONLINE_RESERVATION_SETTINGS_UPDATE,
        params: {
          propertyId: propertyStore.lastSelected!,
        },
      },
      settingsApi,
    )
  }

  load(): Promise<SettingDTO> {
    return this.callAuthWithPropertyAPI<null, SettingDTO>(HttpMethod.GET, {
      path: APIPaths.ONLINE_RESERVATION_SETTINGS_BASIC,
    })
  }

  formConfigurations(): Promise<OnlineReservationFormConfigurationsResponse> {
    return this.callAuthWithPropertyAPI<null, OnlineReservationFormConfigurationList[]>(
      HttpMethod.GET,
      {
        path: APIPaths.ONLINE_RESERVATION_FORM_CONFIGURATIONS_LIST,
      },
    )
  }

  getFormConfiguration(id: string): Promise<OnlineReservationFormConfigurationResponse> {
    return this.callAuthWithPropertyAPI<string, OnlineReservationFormConfiguration>(
      HttpMethod.GET,
      {
        path: APIPaths.ONLINE_RESERVATION_FORM_CONFIGURATION_UUID,
        params: {
          formConfigurationId: id,
        },
      },
    )
  }

  updateFormConfiguration = (
    formConfigurationApi: OnlineReservationFormConfigurationUpdate,
  ): Promise<null> => {
    return this.callAuthWithPropertyAPI<OnlineReservationFormConfiguration, null>(
      HttpMethod.PUT,
      {
        path: APIPaths.ONLINE_RESERVATION_FORM_CONFIGURATION_UUID,
        params: {
          formConfigurationId: formConfigurationApi.formConfigurationId ?? 0,
        },
      },
      formConfigurationApi,
    )
  }

  createFormConfiguration = (
    formConfigurationApi: OnlineReservationFormConfigurationCreate,
  ): Promise<OnlineReservationFormConfigurationCreateResponse> => {
    return this.callAuthWithPropertyAPI<
      OnlineReservationFormConfigurationCreate,
      OnlineReservationFormConfigurationCreateResponse
    >(
      HttpMethod.POST,
      {
        path: APIPaths.ONLINE_RESERVATION_FORM_CONFIGURATION_BASIC,
      },
      formConfigurationApi,
    )
  }

  deleteFormConfiguration = (formConfigurationId: string): Promise<void> => {
    return this.callAuthWithPropertyAPI<null, void>(HttpMethod.DELETE, {
      path: APIPaths.ONLINE_RESERVATION_FORM_CONFIGURATION_UUID,
      params: {
        formConfigurationId: formConfigurationId,
      },
    })
  }
}
